/**
 * @fileoverview React hook for managing client profile data
 *
 * Provides data fetching and mutation capabilities for client profile:
 * - Fetches client profile by email
 * - Updates editable fields (mailing address, secondary email, secondary phone)
 * - Handles loading, error, and success states
 *
 * @requires react-query
 * @requires ../firebase
 * @version 1.0.0
 * @created 2025-12-04
 */

// src/hooks/useClientProfile.js
// React hook for client profile data management

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Fetch client profile from Firebase Functions
 * @param {string} email - Client's email address
 * @returns {Promise<Object>} Client profile data
 */
const fetchClientProfile = async (email) => {
  if (!email) {
    throw new Error("Email is required to fetch profile");
  }

  const getClientProfile = httpsCallable(functions, "getClientProfile");
  const result = await getClientProfile({ email });

  if (!result.data || !result.data.success) {
    throw new Error(result.data?.error || "Failed to fetch client profile");
  }

  return result.data.profile;
};

/**
 * Update client profile via Firebase Functions
 * @param {Object} params - Update parameters
 * @param {string} params.clientId - Airtable client record ID
 * @param {Object} params.updates - Fields to update
 * @returns {Promise<Object>} Updated client profile data
 */
const updateProfile = async ({ clientId, updates }) => {
  if (!clientId) {
    throw new Error("Client ID is required to update profile");
  }

  const updateClientProfile = httpsCallable(functions, "updateClientProfile");
  const result = await updateClientProfile({ clientId, updates });

  if (!result.data || !result.data.success) {
    throw new Error(result.data?.error || "Failed to update client profile");
  }

  return result.data.profile;
};

/**
 * Custom hook for managing client profile data
 * @param {string} email - Client's email address
 * @returns {Object} Profile data, loading state, error, and mutation function
 *
 * @example
 * const { profile, isLoading, error, updateProfileMutation } = useClientProfile(user.email);
 *
 * // To update profile:
 * updateProfileMutation.mutate({
 *   clientId: profile.id,
 *   updates: { mailingAddress: "123 New St" }
 * });
 */
export function useClientProfile(email) {
  const queryClient = useQueryClient();

  // Query for fetching client profile
  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["clientProfile", email],
    queryFn: () => fetchClientProfile(email),
    enabled: !!email, // Only run query when email is provided
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Mutation for updating client profile
  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (updatedProfile) => {
      // Update the cache with the new profile data
      queryClient.setQueryData(["clientProfile", email], updatedProfile);
    },
    onError: (error) => {
      console.error("Error updating profile:", error);
    },
  });

  return {
    profile,
    isLoading,
    error,
    refetch,
    updateProfileMutation,
    isUpdating: updateProfileMutation.isPending,
    updateError: updateProfileMutation.error,
    updateSuccess: updateProfileMutation.isSuccess,
  };
}

export default useClientProfile;
